import { nanoid } from 'nanoid'

export interface ListNodeViewModelItem {
  instanceId: string;
  assetId: string;
}

const modelsCache = new Map<string, ListNodeViewModelItem[]>();

/**
 * 协调新旧 assetIds，复用已有的 instanceId，生成新的 instanceId。
 */
function reconcileViewModels(
  oldItems: ListNodeViewModelItem[],
  newAssetIds: readonly string[]
): ListNodeViewModelItem[] {
  const result: ListNodeViewModelItem[] = [];
  // 复制一份旧的 items 以便查找，找到后标记为使用过（通过 splice 移除）
  const remainingOldItems = [...oldItems];

  for (const assetId of newAssetIds) {
    const matchIndex = remainingOldItems.findIndex(item => item.assetId === assetId);
    if (matchIndex !== -1) {
      // 找到了，复用旧的 instanceId
      result.push(remainingOldItems[matchIndex]);
      // 从剩余项中移除，确保如果有多个相同的 assetId，下一个会匹配到不同的旧实例
      remainingOldItems.splice(matchIndex, 1);
    } else {
      // 没找到，说明是新插入的
      result.push({
        instanceId: `inst_${nanoid(8)}`,
        assetId,
      });
    }
  }

  return result;
}

/**
 * 解析并同步获取节点的 ViewModel 映射数组。
 * 此函数不是 React Hook，而是一个带有缓存的衍生计算函数。
 * 依靠 Yjs 的 snapshot 触发组件渲染，组件在渲染时同步调用此方法即可获取稳定的 ViewModel。
 *
 * @param nodeId 节点 ID
 * @param pageIndex 分页索引（无分页传 -1 即可）
 * @param rawAssetIds 从 Yjs snapshot 读取的纯 assetId 数组
 */
export function resolveListNodeViewModel(
  nodeId: string,
  pageIndex: number,
  rawAssetIds: readonly string[]
): ListNodeViewModelItem[] {
  const key = `${nodeId}_${pageIndex}`;
  let currentModel = modelsCache.get(key) || [];

  const currentAssetIds = currentModel.map(m => m.assetId);
  let isDifferent = currentAssetIds.length !== rawAssetIds.length;
  if (!isDifferent) {
    for (let i = 0; i < rawAssetIds.length; i++) {
      if (currentAssetIds[i] !== rawAssetIds[i]) {
        isDifferent = true;
        break;
      }
    }
  }

  if (isDifferent) {
    currentModel = reconcileViewModels(currentModel, rawAssetIds);
    modelsCache.set(key, currentModel);
  }

  return currentModel;
}

/**
 * 在客户端主动进行拖拽排序等操作时，可以通过此方法手动更新 ViewModel，
 * 并将提取的纯 assetId 数组返回，以便写回 Yjs。
 */
export function updateListNodeViewModelByInstances(
  nodeId: string,
  pageIndex: number,
  newItems: ListNodeViewModelItem[]
): string[] {
  const key = `${nodeId}_${pageIndex}`;
  modelsCache.set(key, newItems);
  return newItems.map(item => item.assetId);
}

/**
 * 清除节点的 ViewModel 缓存，通常在节点被删除时调用
 */
export function clearListNodeViewModel(nodeId: string): void {
  for (const key of modelsCache.keys()) {
    if (key.startsWith(`${nodeId}_`)) {
      modelsCache.delete(key);
    }
  }
}
